/**
 * Netlify Image CDN wrapper — rewrites image URLs through Netlify's
 * `/.netlify/images?url=...&w=...&q=...` endpoint to get :
 *   - On-the-fly AVIF / WebP conversion (per Accept header)
 *   - Server-side resize at exact pixel widths
 *   - Edge cache (free, included in Pro plan)
 *
 * Why this matters : Inside Panama serves ~18 raw Unsplash + Wikimedia
 * images that are 1600-3840px wide regardless of viewport. The Image CDN
 * cuts ~30-50% bandwidth and improves LCP by ~200ms on mobile.
 *
 * Local dev fallback : if not on Netlify (e.g. `npm run dev`), the URL
 * passes through unchanged. Production deploys auto-rewrite.
 *
 * @example
 *   <NetlifyImg
 *     src="https://upload.wikimedia.org/wikipedia/commons/..."
 *     alt="..."
 *     width={800}
 *     quality={80}
 *     loading="lazy"
 *     className="..."
 *   />
 */
export default function NetlifyImg({
  src,
  alt = '',
  width,
  height,
  quality = 80,
  fit = 'cover',
  position,
  className = '',
  loading = 'lazy',
  decoding = 'async',
  ...rest
}) {
  // Local + storybook — bypass the CDN (no /.netlify/images endpoint there)
  const isProd = typeof window === 'undefined'
    ? process.env.NODE_ENV === 'production'
    : window.location.hostname !== 'localhost' && !window.location.hostname.startsWith('127.');

  let url = src;
  if (isProd && src && /^https?:\/\//.test(src)) {
    const params = new URLSearchParams();
    params.set('url', src);
    if (width) params.set('w', String(width));
    if (height) params.set('h', String(height));
    if (quality) params.set('q', String(quality));
    // ⚠️ `fit` n'a d'effet QUE si `w` ET `h` sont fournis — le CDN n'a sinon
    // aucun cadre où faire tenir l'image. Il était posé systématiquement sans
    // hauteur : mesuré en production, `…&w=1600&q=80&fit=cover` renvoie
    // 1600×2560 (rien de rogné) alors que `…&w=1600&h=900&fit=cover` renvoie
    // bien 1600×900. Le paramètre existait donc sans jamais rien faire — d'où
    // les hauteurs de héros allant de 196 à 1008 px d'un article à l'autre.
    if (fit && width && height) params.set('fit', fit);
    // Sans point d'intérêt, le CDN rogne au centre : les sujets hauts (façades,
    // statues, personnes) perdent la tête. Netlify accepte top/bottom/left/
    // right/center — mais PAS de détection automatique : le choix vient de nous.
    if (position && width && height) params.set('position', position);
    url = `/.netlify/images?${params.toString()}`;
  }

  return (
    <img
      src={url}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      decoding={decoding}
      className={className}
      {...rest}
    />
  );
}
